/**
 * KnowledgeHub — Agent Knowledge Hub tab
 *
 * Design: Codecademy-inspired layout adapted to dark mode
 *  - Left sidebar with category navigation + counts
 *  - Featured banner (most recent document)
 *  - Course-style cards: category label, title, description, type/size footer
 *  - Search filters inline
 */

import { useState, useEffect, useCallback } from 'react';
import { getAPIBase, getAuthHeaders } from '../lib/api-fetch';
import { buildPath } from '../lib/api-endpoints';
import type { KnowledgeHubDocument } from '../lib/api-endpoints';
import { Input } from './ui/input';
import {
  FileText, Video, Search, Play, ExternalLink,
  BookOpen, GraduationCap, ClipboardList, BookMarked,
  Megaphone, Scale, Film, ChevronRight, ArrowRight,
} from 'lucide-react';

// ─── Category config ─────────────────────────────────────────────────────────

const CATEGORIES = [
  {
    name: 'Standard Operating Procedures',
    short: 'SOPs',
    icon: BookOpen,
    accent: 'text-blue-400',
    activeBg: 'bg-blue-600',
    dot: 'bg-blue-400',
  },
  {
    name: 'Training Materials',
    short: 'Training',
    icon: GraduationCap,
    accent: 'text-emerald-400',
    activeBg: 'bg-emerald-600',
    dot: 'bg-emerald-400',
  },
  {
    name: 'Application Forms',
    short: 'Forms',
    icon: ClipboardList,
    accent: 'text-violet-400',
    activeBg: 'bg-violet-600',
    dot: 'bg-violet-400',
  },
  {
    name: 'Company Policies & Manuals',
    short: 'Policies',
    icon: BookMarked,
    accent: 'text-orange-400',
    activeBg: 'bg-orange-600',
    dot: 'bg-orange-400',
  },
  {
    name: 'Marketing & Templates',
    short: 'Marketing',
    icon: Megaphone,
    accent: 'text-pink-400',
    activeBg: 'bg-pink-600',
    dot: 'bg-pink-400',
  },
  {
    name: 'Legal & Compliance',
    short: 'Legal',
    icon: Scale,
    accent: 'text-red-400',
    activeBg: 'bg-red-600',
    dot: 'bg-red-400',
  },
  {
    name: 'Video Training',
    short: 'Videos',
    icon: Film,
    accent: 'text-indigo-400',
    activeBg: 'bg-indigo-600',
    dot: 'bg-indigo-400',
  },
] as const;

// ─── Resource card (Codecademy-style) ────────────────────────────────────────

function ResourceCard({
  doc,
  opening,
  onOpen,
}: {
  doc: KnowledgeHubDocument;
  opening: boolean;
  onOpen: () => void;
}) {
  const catMeta = CATEGORIES.find(c => c.name === doc.category);
  const isVideo = doc.fileType === 'video';

  return (
    <div
      data-testid={`kh-doc-${doc.documentId}`}
      onClick={onOpen}
      className="group bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden hover:border-zinc-500 hover:bg-zinc-750 transition-all duration-200 cursor-pointer flex flex-col"
    >
      <div className="p-4 flex flex-col flex-1">
        {/* Category label */}
        <div className={`text-xs font-semibold mb-3 flex items-center gap-1.5 ${catMeta?.accent ?? 'text-zinc-400'}`}>
          {isVideo
            ? <Film className="w-3 h-3" />
            : <FileText className="w-3 h-3" />
          }
          <span>{catMeta?.short ?? doc.category}</span>
        </div>

        {/* Title */}
        <h4 className="font-bold text-white text-sm leading-snug mb-2 line-clamp-2 group-hover:text-indigo-300 transition-colors">
          {doc.title}
        </h4>

        {/* Description */}
        {doc.description && (
          <p className="text-xs text-zinc-400 line-clamp-3 mb-3 leading-relaxed">
            {doc.description}
          </p>
        )}

        {/* Spacer */}
        <div className="mt-auto" />

        {/* Footer divider */}
        <div className="border-t border-zinc-700 pt-3 mt-3">
          <div className="flex items-center justify-between text-xs text-zinc-500">
            <div className="flex items-center gap-1">
              {isVideo
                ? <><Play className="w-3 h-3" /><span>Video</span></>
                : <><FileText className="w-3 h-3" /><span>PDF</span></>
              }
              {doc.subcategory && (
                <span className="ml-1 text-zinc-600">· {doc.subcategory}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {doc.fileSizeMb && <span>{doc.fileSizeMb.toFixed(1)} MB</span>}
              {opening
                ? <div className="w-3 h-3 border border-zinc-400 border-t-transparent rounded-full animate-spin" />
                : <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 animate-pulse">
      <div className="h-3 bg-zinc-700 rounded w-1/3 mb-3" />
      <div className="h-4 bg-zinc-700 rounded w-4/5 mb-1" />
      <div className="h-4 bg-zinc-700 rounded w-3/5 mb-3" />
      <div className="h-3 bg-zinc-700 rounded w-full mb-1" />
      <div className="h-3 bg-zinc-700 rounded w-2/3 mb-4" />
      <div className="border-t border-zinc-700 pt-3">
        <div className="h-3 bg-zinc-700 rounded w-1/4" />
      </div>
    </div>
  );
}

// ─── Featured banner ─────────────────────────────────────────────────────────

function FeaturedBanner({
  doc,
  opening,
  onOpen,
}: {
  doc: KnowledgeHubDocument;
  opening: boolean;
  onOpen: () => void;
}) {
  const isVideo = doc.fileType === 'video';
  const catMeta = CATEGORIES.find(c => c.name === doc.category);

  return (
    <div
      data-testid="kh-featured-banner"
      onClick={onOpen}
      className="relative bg-gradient-to-r from-indigo-700 via-indigo-600 to-purple-700 rounded-2xl p-6 mb-8 flex items-center justify-between cursor-pointer group overflow-hidden"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_50%,_white_1px,_transparent_1px)] bg-[size:20px_20px]" />

      <div className="relative flex-1 min-w-0 pr-6">
        <div className="text-indigo-200 text-xs font-semibold uppercase tracking-wide mb-2">
          Featured Resource · {catMeta?.short ?? doc.category}
        </div>
        <h3 className="text-white font-bold text-xl leading-tight mb-2 line-clamp-2">
          {doc.title}
        </h3>
        {doc.description && (
          <p className="text-indigo-100 text-sm line-clamp-2 mb-4 opacity-90">
            {doc.description}
          </p>
        )}
        <button className="inline-flex items-center gap-2 bg-white text-indigo-700 hover:bg-indigo-50 font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
          {opening ? (
            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>Open resource <ArrowRight className="w-4 h-4" /></>
          )}
        </button>
      </div>

      {/* Icon panel */}
      <div className="relative shrink-0 w-28 h-20 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
        {isVideo
          ? <Play className="w-10 h-10 text-white fill-white/80" />
          : <FileText className="w-10 h-10 text-white/80" />
        }
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function KnowledgeHub() {
  const [documents, setDocuments] = useState<KnowledgeHubDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openingId, setOpeningId] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${getAPIBase()}/knowledge-hub`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setDocuments(json.data.documents || []);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const openDocument = async (doc: KnowledgeHubDocument) => {
    if (openingId) return;
    setOpeningId(doc.documentId);
    try {
      const path = buildPath('/knowledge-hub/:id', { id: doc.documentId });
      const res = await fetch(`${getAPIBase()}${path}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && json.data.url) {
        window.open(json.data.url, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setOpeningId(null);
    }
  };

  const countByCategory = CATEGORIES.reduce<Record<string, number>>((acc, cat) => {
    acc[cat.name] = documents.filter(d => d.category === cat.name).length;
    return acc;
  }, {});

  const visibleDocs = documents.filter(doc => {
    const matchesCat = !selectedCategory || doc.category === selectedCategory;
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      (doc.title || '').toLowerCase().includes(q) ||
      (doc.description || '').toLowerCase().includes(q) ||
      (doc.subcategory || '').toLowerCase().includes(q) ||
      (doc.category || '').toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  const featuredDoc = !selectedCategory && !search && documents.length > 0 ? documents[0] : null;

  return (
    <div className="flex gap-0 min-h-0" data-testid="knowledge-hub-container">

      {/* ── Left Sidebar ── */}
      <aside className="w-52 shrink-0 pr-4 border-r border-zinc-800">
        <div className="sticky top-0 pt-1 pb-4">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-2">Browse</p>

          {/* All */}
          <button
            onClick={() => { setSelectedCategory(null); setSearch(''); }}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-0.5 ${
              !selectedCategory && !search
                ? 'bg-zinc-700 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <BookOpen className="w-4 h-4" />
              <span>All resources</span>
            </div>
            {!isLoading && (
              <span className="text-xs bg-zinc-600 text-zinc-300 rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                {documents.length}
              </span>
            )}
          </button>

          {/* Category items */}
          <div className="mt-3 space-y-0.5">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const count = countByCategory[cat.name] ?? 0;
              const isActive = selectedCategory === cat.name;
              return (
                <button
                  key={cat.name}
                  data-testid={`kh-category-${cat.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  onClick={() => { setSelectedCategory(isActive ? null : cat.name); setSearch(''); }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-zinc-700 text-white font-medium'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className={`w-4 h-4 ${isActive ? cat.accent : ''}`} />
                    <span>{cat.short}</span>
                  </div>
                  {!isLoading && count > 0 && (
                    <span className={`text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
                      isActive ? 'bg-zinc-600 text-zinc-200' : 'bg-zinc-800 text-zinc-500'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="flex-1 min-w-0 pl-6">

        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <Input
            placeholder="Search resources..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 text-sm bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-indigo-500"
            data-testid="kh-search-input"
          />
        </div>

        {/* Section heading */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">
            {selectedCategory
              ? selectedCategory
              : search
                ? `Results for "${search}"`
                : 'Resource Library'
            }
          </h2>
          {!isLoading && (
            <span className="text-sm text-zinc-500">
              {visibleDocs.length} {visibleDocs.length === 1 ? 'resource' : 'resources'}
            </span>
          )}
        </div>

        {/* Featured banner (All view, no search) */}
        {featuredDoc && !isLoading && (
          <FeaturedBanner
            doc={featuredDoc}
            opening={openingId === featuredDoc.documentId}
            onOpen={() => openDocument(featuredDoc)}
          />
        )}

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : visibleDocs.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
            <p className="text-base font-medium text-zinc-400">
              {search ? 'No resources match your search' : 'No resources in this category yet'}
            </p>
            <p className="text-sm text-zinc-600 mt-1">
              {search ? 'Try a different search term' : 'Check back soon'}
            </p>
          </div>
        ) : !selectedCategory && !search ? (
          /* Category sections view */
          <div className="space-y-8">
            {CATEGORIES.filter(cat => (countByCategory[cat.name] ?? 0) > 0).map(cat => {
              const catDocs = documents.filter(d => d.category === cat.name);
              const Icon = cat.icon;
              // Skip featured doc from first category section
              const docsToShow = catDocs === documents.filter(d => d.category === documents[0]?.category)
                ? catDocs.slice(0, 3)
                : catDocs.slice(0, 3);
              return (
                <div key={cat.name}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${cat.accent}`} />
                      <h3 className="font-semibold text-white text-sm">{cat.name}</h3>
                      <span className="text-xs text-zinc-600">{catDocs.length}</span>
                    </div>
                    {catDocs.length > 3 && (
                      <button
                        onClick={() => setSelectedCategory(cat.name)}
                        className={`flex items-center gap-1 text-xs font-medium ${cat.accent} hover:opacity-80 transition-opacity`}
                      >
                        View all <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {docsToShow.map(doc => (
                      <ResourceCard
                        key={doc.documentId}
                        doc={doc}
                        opening={openingId === doc.documentId}
                        onOpen={() => openDocument(doc)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Filtered grid */
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleDocs.map(doc => (
              <ResourceCard
                key={doc.documentId}
                doc={doc}
                opening={openingId === doc.documentId}
                onOpen={() => openDocument(doc)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
